import { Button, InputAdornment, MenuItem, Select, Stack, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { ColorsV2 } from 'app/constants/color';
import { Buttons } from 'app/constants/sizing';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from './CustomDatePicker';
import { Switch } from 'app/components/v2';

const CustomFilterMenu = ({ setFilterModel, columns }) => {
    const [search, setSearch] = useState('');
    const [filterColumns, setFilterColumns] = useState({ field: '', headerName: '', type: '' });
    const [filterOperators, setFilterOperators] = useState([]);
    const [curFilterOperator, setCurFilterOperator] = useState({label: '', value: ''});
    const [sortDate, setSortDate] = useState();
    const [sortBoolean, setSortBoolean] = useState(true);
    // const [isExclusive, setIsExclusive] = useState(false);

    const { t } = useTranslation('translation', {keyPrefix: 'reusable_components', });

    const textOperators = [
        { label: 'Contains', value: 'contains' },
        { label: 'Equals', value: 'equals' },
        { label: 'Starts With', value: 'startsWith' },
        { label: 'Ends With', value: 'endsWith' },
    ];
    
    const numberOperators = [
        { label: 'Equals', value: '=' },
        { label: 'Greater Than', value: '>' },
        { label: 'Less Than', value: '<' },
        { label: 'Greater Than or Equal', value: '>=' },
        { label: 'Less Than or Equal', value: '<=' },
    ];
      
    const dateOperators = [
        { label: 'Before', value: 'before' },
        { label: 'After', value: 'after' },
        { label: 'On', value: 'is' },
    ];
     
    
    const booleanOperators = [
        { label: 'Is', value: 'is' },
    ];
      
    const getOperatorsByType = (type) => {
    switch (type) {
        case 'string':
        return textOperators;
        case 'number':
        return numberOperators;
        case 'date':
        return dateOperators;
        case 'boolean':
        return booleanOperators;
        default:
        return textOperators;
    }
    };

    const isEnumField = (column) => {
        return column.type === 'singleSelect' || Array.isArray(column.valueOptions);
    };

    const isDateField = (column) => {
        return column.type === 'date' || column.type === 'dateTime';
    };   

    useEffect(() => {
        applyFilter();
    }, [search, curFilterOperator, filterColumns])

    const applyFilter = () => {
        if(filterColumns?.field && (isDateField(filterColumns) ? sortDate : true) && (curFilterOperator?.value || filterColumns?.type === 'boolean')){
            let newValue = search;
            if(isDateField(filterColumns)){
                newValue = sortDate.toISOString().split("T")[0];
            }
            if(filterColumns.type === 'boolean'){
                newValue = sortBoolean;
            }
            setFilterModel({
                items: [
                    {
                        columnField: filterColumns?.field,
                        operatorValue: filterColumns?.type === 'boolean' ? 'is' : curFilterOperator.value,
                        value: newValue,
                    }
                ],
                linkOperator: 'or' // Sets the exclusivity when using multiple filters, can be 'and' or 'or'
            });
        }
        else {
            console.error("Error applying filter")
        }

    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const handleFilterColumnChange = (event) => {
        const newFilterColumn = event.target.value;
    
        setFilterColumns((prev) => {
            if (newFilterColumn?.field) {
                const newType = newFilterColumn.type;
                const prevType = prev?.type;
    
                const newOperators = getOperatorsByType(newType || '');
                setFilterOperators(newOperators);
    
                if (newType !== prevType) {
                    if (newType === 'string') {
                        setCurFilterOperator({ label: 'Contains', value: 'contains' });
                    } else {
                        setCurFilterOperator({ label: '', value: '' });
                    }
                }
            } else {
                setFilterOperators([]);
            }
    
            return newFilterColumn;
        });
    };
    
    

    return (
        <Stack
        direction='column'
        gap={1}
        component='form'
        onSubmit={(e) => { e.preventDefault(); applyFilter(); }}
        sx={{ backgroundColor: ColorsV2.secondary, padding: '10px' }}>
            <Stack direction="row" alignItems="center" gap={1}>
                    {/* Select Dropdown */}
                    <Select
                    variant="outlined"
                    value={filterColumns}
                    onChange={(e) => handleFilterColumnChange(e)}
                    displayEmpty 
                    sx={{
                        ...Buttons.baseStyle.selectLowPriority,
                    }}
                    renderValue={(selected) => {
                        return selected?.headerName ? selected?.headerName : t('filtermenu_select_field');
                    }}
                >
                    {/* Placeholder Item */}
                    <MenuItem value="" disabled>
                        {t('filtermenu_select_field')}
                    </MenuItem>

                    {/* Dynamic Column Options */}
                    {columns?.map((col) => (
                        col.filterable ? 
                        <MenuItem key={col.field} value={ col } sx={{
                            backgroundColor: ColorsV2.primary,
                            '&:hover': {
                                backgroundColor: ColorsV2.secondary,
                            },
                            '&:focus:hover': {
                                backgroundColor: ColorsV2.secondary,
                            },
                        }}>
                            {col.headerName}
                        </MenuItem>
                        :
                        null
                    ))}
                </Select>
                {/* Search TextField */}
                {
                filterColumns?.type === 'string' && !isEnumField(filterColumns) &&
                <TextField
                    placeholder="Type to filter"
                    value={search}
                    onChange={(event) => handleSearchChange(event)}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <FilterAltOutlinedIcon sx={{ color: ColorsV2.white }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        ...Buttons.baseStyle.textFieldLowPriority,
                    }}
                />
                }
                {isDateField(filterColumns) && <CustomDatePicker date={sortDate} setDate={setSortDate}/>}
                {filterColumns?.type === 'boolean' && <Switch checked={sortBoolean} onChange={(event) => setSortBoolean(event.target.checked)} />}
            </Stack>
            <Stack direction='row' gap={1} alignItems='center' >
                {filterOperators?.length && filterColumns.type !== 'boolean' ? <Select
                    variant="outlined"
                    value={curFilterOperator}
                    onChange={(event) => setCurFilterOperator(event.target.value)}
                    displayEmpty 
                    sx={{
                        ...Buttons.baseStyle.selectLowPriority,
                    }}
                    renderValue={(selected) => {
                        return selected?.label ? selected?.label : t('filtermenu_select_filter_type');
                    }}
                >
                    {/* Placeholder Item */}
                    <MenuItem value="" disabled>
                        {t('filtermenu_select_filter_type')}
                    </MenuItem>
                    {/* Dynamic Column Options */}
                    {filterOperators?.map((op) => (
                        <MenuItem key={ op.value } value={ op } sx={{
                            backgroundColor: ColorsV2.primary,
                            '&:hover': {
                                backgroundColor: ColorsV2.secondary,
                            },
                            '&:focus:hover': {
                                backgroundColor: ColorsV2.secondary,
                            },
                        }}>
                            { op.label }
                        </MenuItem>
                    ))}
                </Select>
                : null    
                }
                {/* Apply Button */}
                <Button
                variant="text"
                type='submit'
                disabled={!(filterColumns?.field 
                    && (isDateField(filterColumns) ? sortDate : true) 
                    && filterColumns?.type === 'boolean' ? true : curFilterOperator?.value)}
                sx={{
                    ...Buttons.baseStyle.primaryButtonLowPriority,
                }}>
                    {t('apply')}
                </Button>
            </Stack>
            {/* <Stack direction='row' gap={1} alignItems='center'>
            <Button sx={{
                ...Buttons.baseStyle.primaryButtonLowPriority,
            }}>
                Add filter
            </Button>
            And
            <Switch value={isExclusive} onChange={() => setIsExclusive((prev) => !prev)} />
            Or
            </Stack> */}
        </Stack>
    );
};

export default CustomFilterMenu;
